import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';

import styles from './Accordion.module.scss';
import { useAccordionContext, useAccordionItemContext } from './context';
import { AccordionPanelProps } from './types';

export const AccordionPanel = ({ children, className, ...props }: AccordionPanelProps) => {
    const { animationType, transitionTimeout, transitionTimeoutExit, onEnter, onEntering, onExit } =
        useAccordionContext();
    const { expanded, panelId, buttonId } = useAccordionItemContext();

    const handleEnter = (instance: HTMLElement, isAppearing: boolean) => {
        if (onEnter) {
            onEnter(instance, isAppearing);

            return;
        }

        if (animationType === 'height') {
            instance.style.height = '0px';
            instance.style.transition = `height ease ${transitionTimeout}ms`;
        } else if (animationType === 'fadeIn') {
            instance.style.animation = `fade-in ${transitionTimeout}ms ease`;
        }
    };

    const handleEntering = (instance: HTMLElement, isAppearing: boolean) => {
        if (onEntering) {
            onEntering(instance, isAppearing);

            return;
        }

        if (animationType === 'height' && instance.children[0] instanceof HTMLElement) {
            instance.style.height = `${instance.children[0].offsetHeight}px`;
        }
    };

    const handleExit = (instance: HTMLElement) => {
        if (onExit) {
            onExit(instance);

            return;
        }

        if (animationType === 'height') {
            instance.style.height = '0px';
        } else if (animationType === 'fadeIn') {
            instance.style.animation = '';
        }
    };

    const panelContent = (
        <div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            className={cn(styles.panelInner, className)}
            {...props}
        >
            {children}
        </div>
    );

    if (!animationType) {
        return (
            <div hidden={!expanded} className={styles.panel}>
                {panelContent}
            </div>
        );
    }

    return (
        <CSSTransition
            in={expanded}
            timeout={{ enter: transitionTimeout, exit: transitionTimeoutExit }}
            onEnter={handleEnter}
            onEntering={handleEntering}
            onExit={handleExit}
            unmountOnExit
        >
            <div
                className={styles.panel}
                style={{
                    overflow: 'hidden',
                    height: '100%',
                }}
            >
                {panelContent}
            </div>
        </CSSTransition>
    );
};
